import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import { transformTagsToSelectableItems } from '@repo/common/components/MultiSelector/multiSelectorUtils';
import type { SecodaThemeShades } from '@repo/theme/types';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useTagList } from '../../api';
import { extractTagIds } from './tagUtils';

interface OptimizedTagRenderProps<T> {
	field?: string;
	record: T;
	label?: string;
	hideOnEmpty?: boolean;
	customPropertyIdFilter?: string;
}

/**
 * Lightweight read-only component to display tags without selector functionality.
 * Used when user doesn't have permission to edit tags.
 * Uses the abstracted MultiSelectorTarget for consistent tag display across the app.
 */
export function OptimizedTagRender<
	T extends { id: string; integration?: string; tags?: string[] | unknown[] },
>({
	field = 'tags',
	record,
	label,
	hideOnEmpty = true,
	customPropertyIdFilter,
}: OptimizedTagRenderProps<T>) {
	const { data: tags } = useTagList({
		filters: customPropertyIdFilter
			? { custom_property_id: customPropertyIdFilter }
			: undefined,
	});

	const tagIds = useMemo(() => extractTagIds(record, field), [field, record]);

	const selectedTags = useMemo(() => {
		if (!tags?.results || tagIds.length === 0) {
			return [];
		}

		const displayTags = tagIds
			.map((tagId) => tags.results.find((tag) => tag.id === tagId))
			.filter((tag) => !isNil(tag))
			.map((tag) => ({
				id: tag!.id,
				name: tag!.name,
				color: tag!.color as SecodaThemeShades,
			}));

		return transformTagsToSelectableItems(displayTags);
	}, [tags?.results, tagIds]);

	return (
		<MultiSelectorTarget
			selected={selectedTags}
			placeholder={label ? `No ${label}` : `No ${field}`}
			hideOnEmpty={hideOnEmpty}
			readOnly
			iconType="tag"
			property="tags"
			isMenuItemBadge={false}
			variant="tertiary"
		/>
	);
}
